import {
  CREATIVE_TYPE,
  isUrlCreative,
  normalizeCreativeType,
} from '@/shared/constants/creativeTypes';
import type {
  ApiCampaignScreen,
  CampaignCreative,
  CampaignCreativeFile,
  CampaignCreativeSource,
  CampaignEditCreative,
  CampaignSlotCreativeRef,
  EditableCampaignScreen,
} from '../types';

export const getCampaignUseFallback = (
  creatives: CampaignCreative[]
): boolean => {
  const urlCreatives = creatives.filter(creative =>
    isUrlCreative(creative.type)
  );

  return (
    urlCreatives.length > 0 &&
    urlCreatives.every(creative => creative.useFallback)
  );
};

export const buildCreativeLookup = (
  creatives: CampaignCreative[]
): Map<string, CampaignCreative> => {
  const map = new Map<string, CampaignCreative>();
  for (const c of creatives) {
    if (c.id) map.set(c.id, c);
    if (c.assetId) map.set(c.assetId, c);
    if (c.creativeAssetId) map.set(c.creativeAssetId, c);
    if (c.creativeId) map.set(c.creativeId, c);
  }
  return map;
};

const extractSrcUrl = (src?: string | CampaignCreativeSource): string => {
  if (typeof src === 'string') return src;
  return src?.src ?? '';
};

const getUrlCreativeSrc = (
  creative: CampaignCreative | CampaignSlotCreativeRef
) => (typeof creative.src === 'string' ? creative.src : '');

export const resolveSlotCreative = (
  slotCreative: CampaignSlotCreativeRef,
  lookup: Map<string, CampaignCreative>
): CampaignEditCreative => {
  let full = lookup.get(slotCreative.id);
  if (!full && slotCreative.assetId) full = lookup.get(slotCreative.assetId);
  if (!full && slotCreative.creativeId) {
    full = lookup.get(slotCreative.creativeId);
  }
  if (!full && slotCreative.creativeAssetId) {
    full = lookup.get(slotCreative.creativeAssetId);
  }

  const resolvedType =
    slotCreative.type === 'VIDEO' || slotCreative.type === 'IMAGE'
      ? CREATIVE_TYPE.DEFAULT
      : normalizeCreativeType(slotCreative.type || full?.type) ||
        CREATIVE_TYPE.DEFAULT;

  if (!full) {
    const assetId =
      slotCreative.creativeAssetId ||
      slotCreative.assetId ||
      slotCreative.creativeId ||
      slotCreative.id;

    if (isUrlCreative(resolvedType)) {
      return {
        ...slotCreative,
        id: slotCreative.id || assetId,
        type: resolvedType,
        name:
          slotCreative.name ||
          slotCreative.assetName ||
          `${resolvedType} Creative`,
        src: getUrlCreativeSrc(slotCreative),
        duration: slotCreative.duration ?? null,
        partnerId: slotCreative.partnerId,
        useFallback: slotCreative.useFallback,
        fallbackCreativeId: slotCreative.fallbackCreativeId,
      };
    }

    return {
      ...slotCreative,
      type: CREATIVE_TYPE.DEFAULT,
      name: slotCreative.name || slotCreative.assetName || 'Creative',
      src: {
        id: slotCreative.id,
        src: extractSrcUrl(slotCreative.src),
        type: slotCreative.type || 'IMAGE',
        aspectRatio: slotCreative.aspectRatio || '16:9',
        creativeAssetId: assetId,
        resolutionWidth: slotCreative.resolutionWidth || 0,
        resolutionHeight: slotCreative.resolutionHeight || 0,
        name: slotCreative.name || slotCreative.assetName || 'Creative',
      },
    };
  }

  if (isUrlCreative(resolvedType)) {
    return {
      id: slotCreative.id || full.id,
      creativeId: full.creativeAssetId || full.id,
      creativeAssetId: full.creativeAssetId || full.id,
      type: resolvedType,
      name: full.name || full.assetName || `${resolvedType} Creative`,
      assetName: full.assetName || slotCreative.assetName,
      src: getUrlCreativeSrc(full),
      duration: full.duration ?? null,
      partnerId: slotCreative.partnerId || full.partnerId,
      useFallback: full.useFallback,
      fallbackCreativeId: full.fallbackCreativeId,
    };
  }

  const aspectRatio = full.aspectRatio || '16:9';
  const resolvedSrcUrl = extractSrcUrl(full.src);
  const filesObj: Record<string, CampaignCreativeFile> = {
    [aspectRatio]: {
      id: full.id,
      src: resolvedSrcUrl,
      size: 0,
      type: full.type || 'IMAGE',
      status: 'READY',
      aspectRatio,
      isAspectRatio: false,
      creativeAssetId: full.creativeAssetId || full.id,
      resolutionWidth: full.resolutionWidth || 0,
      resolutionHeight: full.resolutionHeight || 0,
    },
  };

  return {
    id: slotCreative.id || full.id,
    creativeId: full.creativeAssetId || full.id,
    type: resolvedType,
    name: full.name || full.assetName || 'Creative',
    duration: full.duration ?? null,
    partnerId: slotCreative.partnerId || full.partnerId,
    src: {
      id: full.id,
      src: resolvedSrcUrl,
      type: full.type || 'IMAGE',
      aspectRatio: full.aspectRatio || '16:9',
      creativeAssetId: full.creativeAssetId || full.id,
      resolutionWidth: full.resolutionWidth || 0,
      resolutionHeight: full.resolutionHeight || 0,
      name: full.name || full.assetName || 'Creative',
      files: filesObj,
    },
  };
};

export const transformScreensForEdit = (
  apiScreens: ApiCampaignScreen[],
  creativeLookup: Map<string, CampaignCreative>
): EditableCampaignScreen[] => {
  return apiScreens.map(screen => {
    const apiSlots = screen.slots || [];
    const totalSlots = screen.numberOfSlots || apiSlots.length || 1;
    const consumedSlots = apiSlots.length;

    const resolvedSlots = apiSlots.map((slot, idx) => ({
      name: slot.name || `Slot ${idx + 1}`,
      index: slot.index,
      dayTimeParting: slot.dayTimeParting || {},
      originalDayTimeParting: slot.dayTimeParting || {},
      originalCreativeIds: (slot.creatives || []).map(c => c.id),
      creatives: (slot.creatives || []).map(sc =>
        resolveSlotCreative(sc, creativeLookup)
      ),
    }));

    return {
      ...screen,
      consumedSlots,
      sov: Math.round((consumedSlots / totalSlots) * 100),
      slots: resolvedSlots,
      originalSlotIds: apiSlots.map(slot => slot.name || ''),
      price: screen.price || 0,
      numberOfSlots: totalSlots,
      slotTimeInSec: screen.slotTimeInSec || 15,
      address: screen.address || {},
    };
  });
};
